/**
 * Persist exercise-library metadata from the Add Exercise modal (phase 2).
 *
 * When the coach edits the collapsible "Exercise details" panel (any change to its fields),
 * we POST to the same Rill actions as the library modals (`edit-secondary` / `update-primary`).
 *
 * ## Rill transport (why not a normal `<form>` submit?)
 *
 * Library modals (`add-secondary-movement`, `edit-primary-movement`) wrap fields in a
 * `<form method="POST" encType="multipart/form-data">`. On Save the browser submits;
 * Rill's document adapter intercepts that submit (`window` "submit" listener in
 * `@rill/http/adapter/document`) and runs the matching `/actions/...` handler **in the
 * browser** without a full page reload.
 *
 * Add Exercise cannot nest a second form inside `#add-exercise`. We first tried a hidden
 * form on `document.body` + `form=` on panel inputs + `requestSubmit()`. Video uploads
 * still arrived as `files {}` because Rill serializes submits with `parse-form`, which
 * only walks `form.elements` and does not reliably pick up detached / `form=`-associated
 * file inputs.
 *
 * Fix: `fetchLibraryMetadataAction()` calls the **same** Rill browser router via
 * `document.fetch(rillServer, { url, method, body, files })`, passing `files.videoFile`
 * explicitly. The action handlers (`user.js`, `rep-maximum.js`) see the same
 * `req.body` / `req.files` shape as a native library modal submit.
 *
 * `req` comes from `RillContext` (`this.context.req` in add-exercise). The router
 * instance is `req.original.socket.server` (internal to @rill/http; we resolve it inside
 * `fetchLibraryMetadataAction` so callers only pass `req`).
 *
 * `@rill/http/adapter/document` is lazy-`require`d inside that function so SSR never
 * evaluates modules that reference `window`.
 *
 * Routes:
 * - Secondary / assistant → POST `/actions/user/edit-secondary/:index`
 * - Primary lifts → POST `/actions/rep-maximum/update-primary`
 *
 * When `source` is `LIBRARY_METADATA_SOURCE_ADD_EXERCISE`, actions respond with 204
 * (no flash, no `refreshWith`); Add Exercise `exercise/save` handles refresh + toast.
 *
 * Call chain: add-exercise `handleSubmit` → `saveLibraryMetadataIfRequested` →
 * `postEditSecondaryLibraryMetadata` | `postUpdatePrimaryLibraryMetadata` →
 * `dispatchLibrarySubmit` → panel `submitLibraryMetadata` → `fetchLibraryMetadataAction`.
 */

import { PARAMETER_OPTIONS } from "../constants";
import { LIBRARY_METADATA_SOURCE_ADD_EXERCISE } from "./exercise-library-metadata";

const { NONE } = PARAMETER_OPTIONS;

/**
 * `edit-secondary` expects exactly four parameter slots; pad missing ones with NONE.
 *
 * @param {object} record — library movement from `exerciseLibrary[primary][index]`.
 * @returns {string[]}
 */
function libraryParametersForEditSecondary(record) {
  const parameters = Array.isArray(record?.parameters) ? record.parameters : [];
  const padded = [...parameters];
  while (padded.length < 4) {
    padded.push(NONE);
  }
  return padded.slice(0, 4);
}

/**
 * Whether the library record already has video (URL string or `{ url, videoOriginal }`).
 *
 * @param {object} record
 * @returns {boolean}
 */
function hadLibraryVideo(record) {
  const video = record?.video;
  if (!video) {
    return false;
  }
  if (typeof video === "string") {
    return String(video).trim().length > 0;
  }
  return Boolean(video.url || video.videoOriginal);
}

/** Shared metadata fields included in every library metadata POST body. */
function buildMetadataFields(payload) {
  return {
    exerciseTypeField: payload.exerciseTypeField || "",
    primaryMuscleGroup: payload.primaryMuscleGroup || "",
    secondaryMuscleGroup: payload.secondaryMuscleGroup || "",
    instructions: payload.instructions || "",
  };
}

/** Mutates `body` with video fields: URL if present, or delete flag if video was cleared. */
function appendVideoFields(body, payload, record, draftValues) {
  if (payload.videoUrl) {
    body.videoUrl = payload.videoUrl;
    return;
  }
  const videoCleared =
    hadLibraryVideo(record) &&
    !draftValues.videoFile &&
    !draftValues.videoOriginal;
  if (videoCleared) {
    body.videoDeleteOtherExercise = "true";
  }
}

/**
 * Flat POST body for `/actions/user/edit-secondary/:index`.
 * Field names match `add-secondary-movement` / server `otherExerciseProperties`.
 *
 * @param {object} payload
 * @param {object} record — original library movement (parameters + video baseline).
 * @param {object} draftValues
 * @returns {Record<string, string>}
 */
function buildEditSecondarySubmitBody(payload, record, draftValues) {
  const parameters = libraryParametersForEditSecondary(record);
  const body = {
    movement: payload.movement || "",
    repMaximum: payload.repMaximum ?? "",
    primary: payload.primary || "",
    source: LIBRARY_METADATA_SOURCE_ADD_EXERCISE,
    parameter0: parameters[0],
    parameter1: parameters[1],
    parameter2: parameters[2],
    parameter3: parameters[3],
    ...buildMetadataFields(payload),
  };
  appendVideoFields(body, payload, record, draftValues);
  return body;
}

/**
 * Flat POST body for `/actions/rep-maximum/update-primary`.
 * Same contract as `edit-primary-movement` modal.
 *
 * @param {object} payload
 * @param {object} record — `primaryExercises[primary]` baseline (may be `{}`).
 * @param {object} draftValues
 * @returns {Record<string, string>}
 */
function buildUpdatePrimarySubmitBody(payload, record, draftValues) {
  const body = {
    primary: payload.primary || "",
    type: "primary",
    repMaximum: payload.repMaximum ?? "",
    source: LIBRARY_METADATA_SOURCE_ADD_EXERCISE,
    ...buildMetadataFields(payload),
  };
  appendVideoFields(body, payload, record, draftValues);
  return body;
}

/**
 * POST library metadata through Rill's in-browser action router.
 *
 * Equivalent to clicking Save on a library modal form, but invoked programmatically
 * so we can attach `files.videoFile` when the coach picked a local video.
 *
 * Resolves with the saved metadata the action returned (200 + JSON), or `null` when there
 * is no readable body (204 / empty / parse error / non-2xx). Parsing is defensive on purpose:
 * a bad/empty response must not abort the exercise save that runs next — it just means the
 * current exercise can't pick up the metadata until refresh.
 *
 * @param {object} req — Rill request from `RillContext` (`this.context.req`)
 * @param {string} url — action URL, e.g. `/actions/user/edit-secondary/3`
 * @param {Record<string, string>} body — flat field map for the action handler
 * @param {Record<string, File>|undefined} files — multipart files (`videoFile` key matches server)
 * @returns {Promise<object|null>} saved metadata from the action, or `null`
 */
export async function fetchLibraryMetadataAction(req, url, body, files) {
  // Only runs on Save click in the browser; never during SSR render.
  if (!process.browser) {
    throw new Error("Library metadata save is only available in the browser");
  }

  // Rill's document adapter stores the client-side router on the active request.
  // This is the same object used when a real <form> submit is intercepted.
  const rillServer = req?.original?.socket?.server;
  if (!rillServer) {
    throw new Error(
      "Rill request context is required for library metadata submit"
    );
  }

  // Lazy require: `@rill/http/adapter/document` touches `window` at load time.
  const { fetch: rillDocumentFetch } = require("@rill/http/adapter/document");

  // Same API Rill uses internally after intercepting a form submit (see onSubmit in document.js).
  // `body` → req.body, `files` → req.files via @rill/body client middleware.
  // Resolves with `[chunks, meta]` (NOT a fetch Response).
  const [chunks, meta] = await rillDocumentFetch(rillServer, {
    url,
    method: "POST",
    body: body || {},
    files: files && Object.keys(files).length ? files : undefined,
  });

  // Defensive parse: only read a body on 2xx; never throw on the response shape.
  const status = meta?.status;
  if (!status || status < 200 || status >= 300) {
    return null;
  }
  try {
    const text = await new Blob(chunks || []).text();
    return text ? JSON.parse(text) : null;
  } catch {
    return null;
  }
}

/**
 * Await the panel's Rill POST (injected callback from add-exercise).
 *
 * This util builds `url` + `body`; the panel callback adds `files.videoFile` and calls
 * `fetchLibraryMetadataAction`. We inject the callback so the panel keeps the File ref
 * (`videoFileRef`) without threading it through every layer.
 *
 * @param {string} url
 * @param {Record<string, string>} body — full action body from builders above
 * @param {(url: string, body: Record<string, string>) => Promise<object|null>} submitLibraryMetadata
 * @returns {Promise<{ ok: true, saved: object|null }>} `saved` = metadata the action returned
 */
async function dispatchLibrarySubmit(url, body, submitLibraryMetadata) {
  if (typeof submitLibraryMetadata !== "function") {
    throw new Error("submitLibraryMetadata is required");
  }
  const saved = await submitLibraryMetadata(url, body);
  return { ok: true, saved: saved ?? null };
}

/**
 * Submit secondary / assistant library metadata to `edit-secondary`.
 *
 * @param {{
 *   payload: object,
 *   record: object,
 *   draft: object,
 *   submitLibraryMetadata: (url: string, body: Record<string, string>) => Promise<object|null>,
 * }} ctx
 * @returns {Promise<{ skipped: true } | { ok: true, saved: object|null }>}
 */
export async function postEditSecondaryLibraryMetadata({
  payload,
  record,
  draft,
  submitLibraryMetadata,
}) {
  if (!payload?.shouldSave || payload.libraryIndex < 0) {
    return { skipped: true };
  }

  const libraryRecord = record || {};

  const url = `/actions/user/edit-secondary/${payload.libraryIndex}`;

  const body = buildEditSecondarySubmitBody(payload, libraryRecord, draft);
  const { saved } = await dispatchLibrarySubmit(url, body, submitLibraryMetadata);
  return { ok: true, saved };
}

/**
 * Submit primary lift library metadata to `update-primary`.
 *
 * @param {{
 *   payload: object,
 *   record: object,
 *   draft: object,
 *   submitLibraryMetadata: (url: string, body: Record<string, string>) => Promise<object|null>,
 * }} ctx
 * @returns {Promise<{ skipped: true } | { ok: true, saved: object|null }>}
 */
export async function postUpdatePrimaryLibraryMetadata({
  payload,
  record,
  draft,
  submitLibraryMetadata,
}) {
  if (!payload?.shouldSave || !payload.isPrimaryExercise || !payload.primary) {
    return { skipped: true };
  }

  const repMaximum = Number(payload.repMaximum);
  if (!Number.isFinite(repMaximum) || repMaximum <= 0) {
    return { skipped: true };
  }

  const libraryRecord = record || {};

  const url = "/actions/rep-maximum/update-primary";

  const body = buildUpdatePrimarySubmitBody(payload, libraryRecord, draft);
  const { saved } = await dispatchLibrarySubmit(url, body, submitLibraryMetadata);
  return { ok: true, saved };
}

/**
 * Entry point from add-exercise Save: read panel ref payload, validate, submit library action.
 *
 * - Nothing changed (or selection can't be saved) → `{ skipped: true }` (exercise save continues).
 * - Success → `{ ok: true, payload, savedMetadata }` (`savedMetadata` may be `null`).
 *
 * `shouldSave` is computed in `buildLibraryMetadataSavePayload`: the library save fires only when
 * the selection is savable AND the coach changed at least one field, so an unchanged form is a no-op.
 *
 * @param {{
 *   getSavePayload: () => object|null,
 *   submitLibraryMetadata: (url: string, body: Record<string, string>) => Promise<unknown>,
 *   record: object,
 * }} ctx — `submitLibraryMetadata` is wired in add-exercise; it forwards `req` to the panel ref
 */
export async function saveLibraryMetadataIfRequested({
  getSavePayload,
  submitLibraryMetadata,
  record,
}) {
  const payload = getSavePayload?.();
  if (!payload?.shouldSave) {
    return { skipped: true };
  }

  const draft = {
    exerciseTypeField: payload.exerciseTypeField,
    primaryMuscleGroup: payload.primaryMuscleGroup,
    secondaryMuscleGroup: payload.secondaryMuscleGroup,
    instructions: payload.instructions,
    videoUrl: payload.videoUrl,
    videoOriginal: payload.videoOriginal,
    videoFile: payload.videoFilePath,
  };

  const libraryRecord = record || {};

  const result = payload.isPrimaryExercise
    ? await postUpdatePrimaryLibraryMetadata({
        payload,
        record: libraryRecord,
        draft,
        submitLibraryMetadata,
      })
    : await postEditSecondaryLibraryMetadata({
        payload,
        record: libraryRecord,
        draft,
        submitLibraryMetadata,
      });

  // `savedMetadata` = what the action persisted (server-resolved, incl. uploaded video URL);
  // add-exercise merges it into the exercise being added. `null` if the response had no body.
  return { ok: true, payload, savedMetadata: result?.saved ?? null };
}
